import React, { useMemo, useState } from "react";
import { hasPermission, isTenantScoped } from "../utils/permissions";
import { PERMISSIONS } from "../constants/permissions";
import { PlusCircle } from "lucide-react";
import { useData } from "../hooks/useData.js";
import { apiFetch } from "../api/index.js";
import {
  LoadingSpinner,
  StandardPageLayout,
  Button,
  Modal,
  FormInput,
  FormSelect,
  Avatar,
} from "../components/Common.jsx";

import { Box, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, Stack } from "@mui/material";
import { Search as SearchIcon } from '@mui/icons-material';
import { notify } from '@/components/core';

// --------------------------- Tenant Form ---------------------------
const TenantForm = ({ partners, plans, currentUser, tenant, onSave, onCancel }) => {
  const isPartnerAdmin = hasPermission(currentUser, 'partners:manage') && !hasPermission(currentUser, 'tenants:manage_all');
  const isEditing = !!tenant;

  const [formData, setFormData] = useState({
    name: tenant?.name || "",
    adminEmail: tenant?.adminEmail || "",
    partnerId: tenant?.partnerId || (isPartnerAdmin ? currentUser.partnerId : (partners[0]?.id || "")),
    planId: tenant?.planId || "",
    domain: tenant?.domain || "",
  });

  // Update partnerId when partners data arrives if it was empty
  React.useEffect(() => {
    if (!isEditing && !formData.partnerId && partners?.length > 0) {
      setFormData(prev => ({ ...prev, partnerId: isPartnerAdmin ? currentUser.partnerId : partners[0].id }));
    }
  }, [partners, isEditing, isPartnerAdmin, currentUser.partnerId, formData.partnerId]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.id]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">

      <FormInput id="name" label="Tenant Name" value={formData.name} onChange={handleChange} required />
      <FormInput id="adminEmail" type="email" label="Admin Email" value={formData.adminEmail} onChange={handleChange} required />

      <FormSelect id="partnerId" label="Partner" value={formData.partnerId} onChange={handleChange} disabled={isPartnerAdmin}>
        <option value="">Select Partner</option>
        {partners.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </FormSelect>

      <FormSelect id="planId" label="Plan" value={formData.planId} onChange={handleChange}>
        <option value="">Select Plan</option>
        {plans.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </FormSelect>

      <FormInput id="domain" label="Domain" value={formData.domain} onChange={handleChange} />

      <div className="flex justify-end gap-3 pt-4">
        <Button variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
        <Button type="submit">{isEditing ? "Save Changes" : "Create Tenant"}</Button>
      </div>
    </form>
  );
};

// --------------------------- Main Tenants Page ---------------------------
export default function TenantsPage({ token, currentUser, onSelectTenant, onSelectOnboarding }) {
  const isPartnerUser = !!currentUser?.partnerId && !currentUser?.tenantId;
  const isPlatformUser = !isTenantScoped(currentUser) && !isPartnerUser;
  const [statusFilter, setStatusFilter] = useState('All');
  const includeDeleted = statusFilter === 'Deleted' || statusFilter === 'All';
  const tenantsEndpoint = includeDeleted ? '/tenants?include_deleted=true' : '/tenants';
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('updated_desc');
  const { data: tenants, isLoading, error, refresh } = useData(tenantsEndpoint, token);
  const { data: partners } = useData(isPlatformUser ? "/partners" : null, token);
  const { data: plans } = useData("/plans", token);


  const [editingTenant, setEditingTenant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [approvingTenantId, setApprovingTenantId] = useState(null);

  // Filter Logic

  const canManage    = hasPermission(currentUser, PERMISSIONS.TENANTS_MANAGE);
  const canApprove   = hasPermission(currentUser, PERMISSIONS.TENANTS_APPROVE);
  const canCreate    = hasPermission(currentUser, PERMISSIONS.TENANTS_CREATE);
  const canOnboard   = hasPermission(currentUser, PERMISSIONS.TENANTS_ONBOARDING_MANAGE);
  // Partner Admin (r3) can edit/disable their own partner's tenants
  const canManageOwn = !canManage && hasPermission(currentUser, 'partners:manage');
  const canEdit      = canManage || canManageOwn;

  // --------------------------- Toggle Status ---------------------------
  const handleToggleStatus = async (tenant) => {
    const newStatus = tenant.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await apiFetch(`/tenants/${tenant.id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      notify.success(`Tenant ${newStatus === 'Active' ? 'enabled' : 'disabled'} successfully.`);
      refresh();
    } catch (err) {
      notify.error('Failed to update tenant: ' + err.message);
    }
  };

  // --------------------------- Approve Logic ---------------------------
  const handleApprove = async (tenantId) => {
    setApprovingTenantId(tenantId);
    try {
      const res = await apiFetch(`/tenants/${tenantId}/approve`, token, { method: "POST" });
      notify.success("Tenant approved successfully.");
      refresh();
    } catch (err) {
      notify.error("Approval failed: " + err.message);
    } finally {
      setApprovingTenantId(null);
    }
  };

  const handleRestore = async (id) => {
    try {
      await apiFetch('/admin/trash/restore', token, { method: 'POST', body: JSON.stringify({ table: 'tenants', record_id: id }) });
      notify.success('Tenant restored successfully.');
      refresh();
    } catch (err) { notify.error('Restore failed: ' + err.message); }
  };

  // --------------------------- Save Logic ---------------------------
  const handleSave = async (tenantData) => {
    const method = editingTenant ? "PUT" : "POST";
    const url = editingTenant ? `/tenants/${editingTenant.id}` : "/tenants";

    try {
      await apiFetch(url, token, {
        method,
        body: JSON.stringify(tenantData),
      });
      notify.success("Tenant saved successfully.");

      setIsModalOpen(false);
      setEditingTenant(null);
      refresh();
    } catch (err) {
      notify.error("Error saving tenant: " + err.message);
    }
  };



  // Filter Logic - must be declared BEFORE sortedTenants useMemo
  const filteredTenants = (tenants || []).filter(t => {
    const lowerSearch = searchTerm.toLowerCase();
    const matchesSearch = t.name.toLowerCase().includes(lowerSearch) ||
      (t.domain && t.domain.toLowerCase().includes(lowerSearch)) ||
      (t.adminEmail && t.adminEmail.toLowerCase().includes(lowerSearch));
    if (statusFilter === 'Deleted') return matchesSearch && t.is_deleted;
    const matchesStatus = statusFilter === 'All' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedTenants = useMemo(() => {
    const list = [...filteredTenants];
    list.sort((a, b) => {
      const dateA = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0).getTime();
      const dateB = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0).getTime();
      if (sortOption === 'updated_desc') return dateB - dateA;
      if (sortOption === 'updated_asc') return dateA - dateB;
      if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      return 0;
    });
    return list;
  }, [filteredTenants, sortOption]);

  // --------------------------- UI Rendering ---------------------------
  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="p-4 text-red-600">Failed to load tenants.</div>;

  const headerActions = canCreate ? (
    <Button onClick={() => { setEditingTenant(null); setIsModalOpen(true); }} sx={{ height: 30 }}>
      <PlusCircle className="h-4 w-4 mr-2" /> ADD TENANT
    </Button>
  ) : null;

  const filterControls = (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        size="small"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ width: 200, bgcolor: 'background.paper', borderRadius: 1 }}
      />
      <FormControl size="small" sx={{ minWidth: 100, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          displayEmpty
        >
          <MenuItem value="All">All Status</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Approved">Approved</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
          <MenuItem value="Deleted">Deleted</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 170, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          displayEmpty
        >
          <MenuItem value="updated_desc">Recently Updated</MenuItem>
          <MenuItem value="updated_asc">Oldest Updated</MenuItem>
          <MenuItem value="name_asc">Name A-Z</MenuItem>
          <MenuItem value="name_desc">Name Z-A</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <StandardPageLayout title="Tenants" description="Manage tenants and their subscription plans." headerActions={headerActions} filterControls={filterControls}>
      <Box className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tenant</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Plan</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Partner</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedTenants.filter(t => t.id !== 'platform').map((t) => (
              <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <td
                  className={`p-4 ${t.status === "Active" ? "cursor-pointer" : "opacity-60"}`}
                  onClick={() => t.status === "Active" && onSelectTenant(t.id)}
                >
                  <div className="flex items-center">
                    <Avatar src={t.collapsed_logo_url || t.logo_light_url} name={t.name} size={40} profileDetails={{ name: t.name, email: t.adminEmail, domain: t.domain, role: 'Tenant' }} />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{t.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{t.domain}</div>
                    </div>
                  </div>
                </td>

                <td className="p-4">
                  {t.is_deleted
                    ? <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-600">Deleted</span>
                    : <span className={`px-2 py-1 text-xs font-semibold rounded-full 
                    ${t.status === "Active" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" :
                      t.status === "Approved" ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300" :
                        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"}`}>
                    {t.status}
                  </span>}
                </td>

                <td className="p-4 text-sm text-gray-500 dark:text-gray-300">{t.planName || "N/A"}</td>
                <td className="p-4 text-sm text-gray-500 dark:text-gray-300">{t.partnerName || "N/A"}</td>

                <td className="p-4 flex gap-2">
                  {t.is_deleted ? (
                    <Button size="sm" onClick={() => handleRestore(t.id)}>Restore</Button>
                  ) : (<>
                  {/* Pending → Approve */}
                  {t.status === "Pending" && canApprove && (
                    <Button size="sm" onClick={() => handleApprove(t.id)}>
                      {approvingTenantId === t.id ? "Approving..." : "Approve"}
                    </Button>
                  )}

                  {/* Approved → Onboard */}
                  {t.status === "Approved" && canOnboard && (
                    <Button variant="secondary" size="sm" onClick={() => onSelectOnboarding(t.id)}>
                      Onboard
                    </Button>
                  )}

                  {/* Active / Inactive → Edit (Platform Admin or Partner Admin) */}
                  {['Active', 'Inactive'].includes(t.status) && canEdit && (
                    <Button variant="secondary" size="sm" onClick={() => {
                      setEditingTenant(t);
                      setIsModalOpen(true);
                    }}>
                      Edit
                    </Button>
                  )}

                  {/* Active → Disable / Inactive → Enable (Platform Admin or Partner Admin) */}
                  {['Active', 'Inactive'].includes(t.status) && canEdit && (
                    <Button
                      variant={t.status === 'Active' ? 'danger' : 'secondary'}
                      size="sm"
                      onClick={() => handleToggleStatus(t)}
                    >
                      {t.status === 'Active' ? 'Disable' : 'Enable'}
                    </Button>
                  )}
                  </>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* CREATE / EDIT MODAL */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={editingTenant ? "Edit Tenant" : "Add Tenant"}
        >
          <TenantForm
            partners={partners || []}
            plans={plans || []}
            currentUser={currentUser}
            tenant={editingTenant}
            onSave={handleSave}
            onCancel={() => setIsModalOpen(false)}
          />
        </Modal>
      )}
    </StandardPageLayout>
  );
}
